import { rewriteAssetUrl } from '../instance/assets.ts';
import type { ImageRef } from '../model/media.ts';
import type { ParseContext } from './context.ts';
import { asArray, asNonEmptyString, asNumber, asObject, field } from './raw.ts';

/**
 * R2 — image URLs are rewritten **in the parser layer, never in components**, so no component can
 * leak by omission. Every endpoint that carries an image funnels through here: video thumbnails,
 * author avatars, playlist covers, comment avatars and the creator-heart thumbnail.
 *
 * An entry whose URL cannot be served from the instance returns `null` and is dropped from the
 * list rather than passed through: a rendered `<img src="https://yt3.ggpht.com/…">` is a request
 * Google can count, which is most of what Invidious exists to prevent.
 */
export function parseImageRef(raw: unknown, ctx: ParseContext): ImageRef | null {
  const source = asObject(raw);
  if (!source) return null;
  const rawUrl = asNonEmptyString(field(source, 'url'));
  if (rawUrl === null) return null;
  const url = rewriteAssetUrl(ctx.config, rawUrl);
  if (url === null) return null;
  return {
    url,
    width: asNumber(field(source, 'width')),
    height: asNumber(field(source, 'height')),
    quality: asNonEmptyString(field(source, 'quality')),
  };
}

export function parseImageList(raw: unknown, ctx: ParseContext): readonly ImageRef[] {
  const array = asArray(raw);
  if (!array) return [];
  const out: ImageRef[] = [];
  for (const entry of array) {
    const image = parseImageRef(entry, ctx);
    if (image !== null) out.push(image);
  }
  return out;
}

/**
 * The same thing for the fields that carry a bare URL string instead of an object — search's
 * `playlistThumbnail` (`i.ytimg.com/vi/…`) and a comment's `creatorHeart.creatorThumbnail`
 * (`yt3.ggpht.com/…`). Dimensions are unknown, which is exactly what `null` means here.
 */
export function parseImageUrl(raw: unknown, ctx: ParseContext): ImageRef | null {
  const rawUrl = asNonEmptyString(raw);
  if (rawUrl === null) return null;
  const url = rewriteAssetUrl(ctx.config, rawUrl);
  if (url === null) return null;
  return { url, width: null, height: null, quality: null };
}
